import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { popularComics, recommendedComics } from '@/mocks/homeData';
import SeoStructuredData from '@/components/base/SeoStructuredData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const allComics = [...popularComics, ...recommendedComics];
  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-search-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的漫画页面可能已被移除或不存在</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: 24 }, (_, i) => {
    const baseNum = (comic.latestChapter ? parseInt(comic.latestChapter.replace(/[^0-9]/g, '')) : 100);
    const num = baseNum - i;
    return {
      num,
      title: `第${num}话`,
      date: `${Math.floor(i / 2) + 1}天前`,
    };
  });

  const relatedComics = allComics.filter((c) => c.id !== comic.id && (c.tags?.some((t: string) => comic.tags?.includes(t)) || c.category === comic.category)).slice(0, 6);
  const canonicalUrl = `http://thinkbu.com/${location.pathname}`;

  return (
    <>
      <SeoStructuredData
        type="Article"
        data={{
          headline: comic.title,
          url: canonicalUrl,
          description: (comic as any).summary || comic.description || '',
          author: 'author' in comic ? (comic as any).author : '土豪漫画',
          publishDate: (comic as any).publishYear ? `${(comic as any).publishYear}-01-01` : '2024-01-01',
          image: comic.cover,
        }}
      />
      <SeoStructuredData
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'http://thinkbu.com/' },
            { name: '漫画分类', url: 'http://thinkbu.com/category/all' },
            { name: comic.title, url: canonicalUrl },
          ],
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          {/* Breadcrumb */}
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">漫画分类</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{comic.title}</span>
          </nav>

          {/* Detail */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-10 mb-14">
            {/* Cover */}
            <div className="w-full md:w-[320px] shrink-0">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>
              {'author' in comic && (
                <p className="text-foreground-600 mb-4">作者：{'author' in comic ? (comic as any).author : '佚名'}</p>
              )}
              {(comic as any).summary && (
                <p className="text-foreground-700 leading-relaxed mb-5 max-w-2xl">{(comic as any).summary}</p>
              )}
              {'description' in comic && !(comic as any).summary && (
                <p className="text-foreground-700 leading-relaxed mb-5 max-w-2xl">{'description' in comic ? comic.description : ''}</p>
              )}
              {'tags' in comic && comic.tags && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag: string) => (
                    <Link
                      key={tag}
                      to={`/category/all`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600 mb-6">
                {'rating' in comic && (
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    <strong>{'rating' in comic ? comic.rating : '9.0'}</strong> 分
                  </span>
                )}
                {'views' in comic && (
                  <span><i className="ri-eye-line mr-1"></i> {'views' in comic ? (comic as any).views : ''}</span>
                )}
                <span>状态：连载中</span>
                {'latestChapter' in comic && (
                  <span><i className="ri-book-open-line mr-1"></i> 已更新至 {comic.latestChapter}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <button className="px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer shadow-sm">
                  <i className="ri-play-circle-line mr-1"></i>开始阅读
                </button>
                <button className="px-7 py-3 rounded-full bg-background-100 text-foreground-800 text-sm font-medium border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-1"></i>收藏漫画
                </button>
                <button className="px-7 py-3 rounded-full bg-background-100 text-foreground-800 text-sm font-medium border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-line mr-1"></i>分享
                </button>
              </div>
            </div>
          </div>

          {/* Chapters */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900">章节列表</h2>
              <span className="text-sm text-foreground-500">共 {chapters.length} 话</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {chapters.map((ch) => (
                <a
                  key={ch.num}
                  href="#"
                  className="flex items-center justify-between px-5 py-3.5 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-300 hover:bg-background-50 transition-all cursor-pointer group"
                >
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors">{ch.title}</span>
                  <span className="text-xs text-foreground-500">{ch.date}</span>
                </a>
              ))}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">猜你喜欢</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to="/category/all"
                    className="group rounded-xl overflow-hidden cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-xl overflow-hidden mb-3">
                      <img
                        src={c.cover}
                        alt={c.title}
                        title={`${c.title} - 韩国漫画在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="text-sm font-bold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {c.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {'rating' in c ? c.rating : '9.0'} 分
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}